/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Loader, ModalGeneric, TextArea } from 'ypf-pas-general-ui-components';
import { FiSend } from 'react-icons/fi';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import { capitalize, formatDateToSpanish, formDateHour } from '../../../utils/formatUtils';
import useComment from '../../../hooks/useComment';
import { postComment } from '../../../services/workReport';
import useToast from '../../../hooks/useToast';
import { checkRol } from '../../../security';
import { aprobador } from '../../../security/constantsRols';

import NoComments from './NoComments/NoComments';

import './CommentsModal.scss';
import '../WorkReportModals.scss';

const showDateHour = (value) => {
  const { newDate, newHour } = formDateHour(value);

  return `${newDate} | ${newHour}`;
};

const CommentsModal = ({
  show,
  onHide,
  onAccept,
  monthly,
  positionName,
  lineName,
  date,
  dailyWorkReportId,
  um,
  quantity,
  disabled
}) => {
  const [comment, setComment] = useState('');
  const [loadingComment, setLoadingComment] = useState(false);
  const [sentMessages, setSentMessages] = useState(0);
  const { setNewToast } = useToast();
  const { day, month, year } = formatDateToSpanish(date);

  const { loading, comments, hasNextPage, error, loadMore, refreshInitComments, reset } =
    useComment(dailyWorkReportId, monthly);
  const [infiniteRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: loadMore,
    disabled: error,
    rootMargin: '450px 0px 0px 0px'
  });
  const scrollableRootRef = useRef(null);
  const lastScrollDistanceToBottomRef = useRef(null);

  // We keep the scroll position when new items are added etc.
  useEffect(() => {
    const scrollableRoot = scrollableRootRef.current;
    const lastScrollDistanceToBottom = lastScrollDistanceToBottomRef.current ?? 0;

    if (scrollableRoot) {
      scrollableRoot.scrollTop = scrollableRoot.scrollHeight - lastScrollDistanceToBottom;
    }
  }, [comments]);

  const rootRefSetter = useCallback((node) => {
    scrollableRootRef.current = node;
  }, []);

  const sendComment = () => {
    setLoadingComment(true);
    postComment(dailyWorkReportId, `${comment}`, monthly)
      .then(() => {
        setComment('');
        refreshInitComments();
        setLoadingComment(false);
        setSentMessages(sentMessages + 1);
      })
      .catch((err) => {
        setLoadingComment(false);
        setNewToast({
          type: 'ERROR',
          message: err.message
        });
      });
  };

  const handleRootScroll = useCallback(() => {
    const rootNode = scrollableRootRef.current;

    if (rootNode) {
      const scrollDistanceToBottom = rootNode.scrollHeight - rootNode.scrollTop;

      lastScrollDistanceToBottomRef.current = scrollDistanceToBottom;
    }
  }, []);

  const hideModal = () => {
    onHide();
    if (comments.length === sentMessages) onAccept();
  };

  useEffect(() => {
    if (!show) {
      setComment('');
      setSentMessages(0);
      reset();
    }
    if (show) {
      loadMore();
    }
  }, [show]);

  return (
    <ModalGeneric
      backdrop="static"
      infoModal
      onHide={hideModal}
      primaryAction={hideModal}
      primaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Comentarios"
    >
      <>
        <div className="text-center modal-info">
          <div className="d-flex justify-content-center flex-wrap">
            <span>&quot;{positionName}</span>
            {checkRol(aprobador) ? (
              <>
                <span>{lineName}</span>
                <span>
                  {um} {quantity}&quot;
                </span>
              </>
            ) : (
              <span>{lineName}&quot;</span>
            )}
          </div>
          Para el día {capitalize(day?.text)}, {day?.number} de {month} de {year}
        </div>
        <hr className="my-4" />
        <div className="chat-container" onScroll={handleRootScroll} ref={rootRefSetter}>
          <div className="comment-container">
            {hasNextPage && (
              <div className="loading-comment" ref={infiniteRef}>
                <span>Cargando Comentarios...</span>
              </div>
            )}
            {comments.length ? (
              comments.map((item, index) => (
                <div
                  className={`comment-container-${item.esUsuarioExterno ? 'receiver' : 'sender'}`}
                  key={item.fechaEnvio + index.toString()}
                  // style={{
                  //   marginTop: comments[i-1]?.usuario === item.usuario && '0'
                  // }}
                >
                  {/* Si se require que los mensajes consecutivos de un mismo usuario
                    Omitan el nombre de usuario (Como en el diseño)
                  comments[i-1]?.usuario === item.usuario ? null : (
                    <div className='user-name'>
                      {item.usuario}
                    </div>
                  )
                */}
                  <div className="user-name">{item.usuario}</div>
                  <div
                    className="comment-space"
                    style={{
                      color: item.color
                    }}
                  >
                    {item.comentario}
                  </div>
                  <div className="comment-hour">{showDateHour(item.fechaEnvio)}</div>
                </div>
              ))
            ) : (
              <NoComments />
            )}
          </div>
        </div>
        {!disabled && (
          <>
            <hr />
            <div className="input-comment">
              <TextArea
                className="comment-text"
                disabled={loadingComment}
                label=""
                maxLength={512}
                name="message"
                onChange={(e) => setComment(e.target.value)}
                placeholder="Escribí tu mensaje"
                resize={false}
                rows={2}
                value={comment}
              />
              <Button
                className="mx-2 mb-3"
                colorIcon="primary"
                disabled={!comment || loadingComment}
                icon={FiSend}
                onClick={sendComment}
                type="button"
                variant="light-gray"
                widthButton="50px"
              />
            </div>
          </>
        )}
        <hr />
        <Loader show={loading || loadingComment} text="CARGANDO" />
      </>
    </ModalGeneric>
  );
};

export default CommentsModal;

CommentsModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  onAccept: PropTypes.func,
  monthly: PropTypes.bool,
  positionName: PropTypes.string,
  lineName: PropTypes.string,
  date: PropTypes.string,
  dailyWorkReportId: PropTypes.number,
  disabled: PropTypes.bool,
  um: PropTypes.string,
  quantity: PropTypes.number
};

CommentsModal.defaultProps = {
  show: false,
  onHide: () => {
    /* Empty */
  },
  onAccept: () => {
    /* Empty */
  },
  monthly: false,
  positionName: '',
  lineName: '',
  date: '',
  dailyWorkReportId: null,
  disabled: false,
  um: '',
  quantity: 0
};
